import { FC, useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { DashboardTableUnassignedBatch } from './DashboardTableUnassignedBatch';
import { useTranslation } from 'react-i18next';
import { DashboardTableTimerCancelButton } from '../actions/DashboardTableTimerCancelButton';
import { DashboardTableUnassignedSelect } from './DashboardTableUnassignedSelect';
import { DashboardTableSiteSelect } from '../DashboardTableSiteSelect';
import { DashboardTableLabel } from '../actions/DashboardTableLabel';
import { DashboardTableActionsDeleteButton } from '../actions/DashboardTableActionsDeleteButton';
import { DashboardTableActions } from '../DashboardTableActions';
import { Site } from '../../../sites/dto/Site';
import { Customer } from '../../../customers/dto/Customer';
import { DashboardContext } from '../../DashboardContext';

export const DashboardTableUnassignedBatchActions: FC<{
  batch: DashboardTableUnassignedBatch;
}> = observer(({ batch }) => {
  const { t } = useTranslation();
  const { data: sites = [] } = useContext(DashboardContext).sites;
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // Pre-select site from pending transaction if all assets have the same site
  useEffect(() => {
    const commonSiteId = batch.commonPendingTransactionSiteId;
    if (commonSiteId && !selectedSite) {
      const site = sites.find((s) => s.id === commonSiteId);
      if (site) {
        setSelectedSite(site);
      }
    }
  }, [batch.commonPendingTransactionSiteId, sites, selectedSite]);

  return (
    <DashboardTableActions>
      {batch.batchRequestInProgress ? (
        <DashboardTableLabel>{t('Loading')}</DashboardTableLabel>
      ) : batch.cancelCountdown ? (
        <DashboardTableTimerCancelButton
          countdown={batch.cancelCountdown}
          onClick={() => {
            batch.cancelBatchAssigning();
          }}
        />
      ) : batch.canBeProcessedAssets.length ? (
        <>
          <DashboardTableSiteSelect
            placeholder={t('From site')}
            onSiteSelect={(site) => {
              setSelectedSite(site);
              if (selectedCustomer) {
                batch.assignBatch(selectedCustomer, site);
                setSelectedSite(null);
                setSelectedCustomer(null);
              }
            }}
            selectedSiteId={selectedSite?.id}
          />
          <DashboardTableUnassignedSelect
            placeholder={t('To customer')}
            onCustomerSelect={(customer) => {
              setSelectedCustomer(customer);
              if (selectedSite) {
                batch.assignBatch(customer, selectedSite);
                setSelectedSite(null);
                setSelectedCustomer(null);
              }
            }}
          />
        </>
      ) : null}

      <DashboardTableActionsDeleteButton
        onDelete={async () => {
          await batch.delete();
        }}
      >
        {t('Cancel')}
      </DashboardTableActionsDeleteButton>
    </DashboardTableActions>
  );
});
